import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money2 } from '@/lib/helpers';
import { RECEIPT_TEMPLATES, templateById, templateStyle } from '@/lib/receiptTemplates';
import { Receipt, Save, Eye, RotateCcw, Check, LayoutTemplate } from 'lucide-react';

export const RECEIPT_DEFAULTS = {
  invoice_title: 'SHABZ CLOSET',
  template: 'classic',
  footer_text: 'Thank you for shopping with SHABZ CLOSET!',
  header_note: '',
  show_gst_line: true,
  gst_line_text: 'Prices are GST inclusive.',
  tax_percent: 0,
  tax_label: 'GST',
  default_bill_discount: 0,
  show_logo: true,
  show_customer: true,
  show_salesperson: true,
  paper_width: '80mm',
  accent_totals: true,
};

const PAPER = [
  ['58mm', '58mm (thermal)'],
  ['80mm', '80mm (thermal)'],
  ['a4', 'A4 / Letter'],
];

// Small self-contained receipt preview so the owner sees changes instantly.
function ReceiptPreview({ r, branding }) {
  const width = r.paper_width === '58mm' ? 220 : r.paper_width === 'a4' ? 340 : 280;
  const st = templateStyle(r.template);
  const items = [
    { product: 'Anarkali Suit', barcode: 'SC00123', price: 4999 },
    { product: 'Cotton Kurti', barcode: 'SC00188', price: 1499 },
  ];
  const subtotal = items.reduce((s, i) => s + i.price, 0);
  const discount = Number(r.default_bill_discount) || 0;
  const net = Math.max(0, subtotal - discount);
  const taxPct = Number(r.tax_percent) || 0;
  const taxAmt = taxPct > 0 ? Math.round((net - net / (1 + taxPct / 100)) * 100) / 100 : 0;
  const dividerCls = st.divider === 'dashed' ? 'border-t border-dashed border-slate-300' : 'border-t border-slate-200';
  const brandSize = st.brandSize === 'xl' ? 'text-2xl' : st.brandSize === 'md' ? 'text-base' : 'text-lg';
  const alignCls = st.brandAlign === 'left' ? 'items-start text-left' : 'items-center text-center';
  const mono = st.mono ? 'font-mono' : '';
  return (
    <div className={`mx-auto rounded-xl border border-border bg-white p-4 text-slate-900 shadow-sm ${mono}`}
      style={{ width, fontFamily: st.mono ? 'ui-monospace, monospace' : 'ui-sans-serif, system-ui' }} data-testid="receipt-preview">
      <div className={`flex flex-col ${alignCls}`}>
        {r.show_logo && branding?.logo && (
          <img src={branding.logo} alt="Logo" className="mb-1 h-10 w-auto object-contain" />
        )}
        <p className={`font-display font-bold tracking-tight ${brandSize}`}>{r.invoice_title || 'SHABZ CLOSET'}</p>
        {r.header_note && <p className="text-[11px] text-slate-500">{r.header_note}</p>}
        <p className="mt-0.5 text-[11px] text-slate-500">Head Office</p>
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-slate-500">
        <span className="font-mono">SC00456</span><span>12 Sep 2026</span>
      </div>
      {r.show_customer && (
        <div className={`mt-2 pt-1 text-[11px] ${dividerCls}`}>
          <span className="text-slate-500">Customer: </span>Priya Sharma
        </div>
      )}
      <div className={`mt-2 pt-1 ${dividerCls}`}>
        {items.map((i) => (
          <div key={i.barcode} className="flex justify-between py-0.5 text-xs">
            <span className="truncate pr-2">{i.product}</span>
            <span className="font-mono tabular-nums">{money2(i.price)}</span>
          </div>
        ))}
      </div>
      <div className={`mt-1 space-y-0.5 pt-1 text-xs ${dividerCls}`}>
        <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{money2(subtotal)}</span></div>
        {discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span className="font-mono">- {money2(discount)}</span></div>}
        {st.totalStyle === 'boxed' ? (
          <div className="mt-1 flex items-center justify-between rounded-lg bg-emerald-700 px-2.5 py-1.5 text-sm font-bold text-white">
            <span>Total</span><span className="font-mono tabular-nums">{money2(net)}</span>
          </div>
        ) : (
          <div className={`flex justify-between pt-1 text-sm font-bold ${dividerCls} ${st.totalStyle === 'accent' ? 'text-emerald-700' : ''}`}>
            <span>Total</span><span className="font-mono tabular-nums">{money2(net)}</span>
          </div>
        )}
        {taxPct > 0 && (
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>{r.tax_label || 'GST'} ({taxPct}%) incl.</span><span className="font-mono">{money2(taxAmt)}</span>
          </div>
        )}
        {r.show_gst_line && r.gst_line_text && <p className="text-[10px] text-slate-500">{r.gst_line_text}</p>}
      </div>
      {r.show_salesperson && <p className="mt-1 text-[11px] text-slate-500">Sold by: Sales Staff</p>}
      {r.footer_text && <p className={`mt-2 text-[11px] text-slate-600 ${st.brandAlign === 'left' ? 'text-left' : 'text-center'}`}>{r.footer_text}</p>}
    </div>
  );
}

export function ReceiptCustomizerPanel() {
  const { settings, loadSettings, user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const initial = useMemo(() => ({ ...RECEIPT_DEFAULTS, ...(settings?.receipt || {}) }), [settings]);
  const [r, setR] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setR({ ...RECEIPT_DEFAULTS, ...(settings?.receipt || {}) }); }, [settings]);

  const set = (k, v) => setR((p) => ({ ...p, [k]: v }));
  const applyTemplate = (t) => {
    setR((p) => ({ ...p, ...t.apply, template: t.id }));
    toast.message(`${t.name} template applied`, { description: 'Tweak anything below, then Save.' });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...r,
        tax_percent: Number(r.tax_percent) || 0,
        default_bill_discount: Number(r.default_bill_discount) || 0,
      };
      await api.put('/settings', { receipt: payload });
      await loadSettings();
      toast.success('Receipt settings saved');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const reset = () => { setR({ ...RECEIPT_DEFAULTS }); toast.message('Reset to defaults', { description: 'Tap Save to keep it.' }); };

  if (!isOwner) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sc-card" data-testid="receipt-customizer-panel">
      <div className="mb-1 flex items-center gap-2">
        <Receipt className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Receipt customiser</p>
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">Owner only</span>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">Personalise your printed bill — title, footer, tax display and a default bill discount. This is presentation only; it never changes past sales. (Logo is set in Theme &amp; Branding.)</p>

      {/* One-tap templates */}
      <div className="mb-4" data-testid="receipt-templates">
        <div className="mb-2 flex items-center gap-2"><LayoutTemplate className="h-3.5 w-3.5 text-muted-foreground" /><p className="text-xs font-semibold">Ready-made looks</p></div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {RECEIPT_TEMPLATES.map((t) => {
            const active = (r.template || 'classic') === t.id;
            return (
              <button key={t.id} type="button" onClick={() => applyTemplate(t)} data-testid={`receipt-template-${t.id}`}
                className={`relative rounded-xl border p-2.5 text-left transition-colors ${active ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-primary/50'}`}>
                {active && <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-primary" />}
                <p className="text-xs font-semibold">{t.name}</p>
                <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">{t.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Invoice title</Label>
            <Input value={r.invoice_title} onChange={(e) => set('invoice_title', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="receipt-title" />
          </div>
          <div>
            <Label className="text-xs">Header note (optional)</Label>
            <Input value={r.header_note} onChange={(e) => set('header_note', e.target.value)} placeholder="e.g. Fashion for every occasion" className="mt-1 h-11 rounded-xl" data-testid="receipt-header-note" />
          </div>
          <div>
            <Label className="text-xs">Footer text</Label>
            <Input value={r.footer_text} onChange={(e) => set('footer_text', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="receipt-footer" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Default bill discount (₹)</Label>
              <Input inputMode="numeric" value={r.default_bill_discount} onChange={(e) => set('default_bill_discount', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="receipt-default-discount" />
            </div>
            <div>
              <Label className="text-xs">Paper width</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {PAPER.map(([v, l]) => (
                  <button key={v} type="button" onClick={() => set('paper_width', v)} data-testid={`receipt-paper-${v}`}
                    className={`rounded-full border px-2.5 py-1.5 text-[11px] font-medium ${r.paper_width === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tax label</Label>
              <Input value={r.tax_label} onChange={(e) => set('tax_label', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="receipt-tax-label" />
            </div>
            <div>
              <Label className="text-xs">Tax % (display)</Label>
              <Input inputMode="numeric" value={r.tax_percent} onChange={(e) => set('tax_percent', e.target.value)} placeholder="0" className="mt-1 h-11 rounded-xl" data-testid="receipt-tax-percent" />
            </div>
          </div>
          <div>
            <Label className="text-xs">GST line text</Label>
            <Input value={r.gst_line_text} onChange={(e) => set('gst_line_text', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="receipt-gst-text" />
          </div>
          <div className="space-y-2 rounded-xl border border-border p-3">
            <ToggleRow label="Show GST inclusive line" checked={r.show_gst_line} onChange={(v) => set('show_gst_line', v)} testId="receipt-toggle-gst" />
            <ToggleRow label="Show customer details" checked={r.show_customer} onChange={(v) => set('show_customer', v)} testId="receipt-toggle-customer" />
            <ToggleRow label="Show salesperson" checked={r.show_salesperson} onChange={(v) => set('show_salesperson', v)} testId="receipt-toggle-salesperson" />
            <ToggleRow label="Highlight total in accent colour" checked={r.accent_totals} onChange={(v) => set('accent_totals', v)} testId="receipt-toggle-accent" />
            <ToggleRow label="Show logo on receipt" checked={r.show_logo} onChange={(v) => set('show_logo', v)} testId="receipt-toggle-logo" />
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2"><Eye className="h-3.5 w-3.5 text-muted-foreground" /><p className="text-xs font-semibold">Live preview</p></div>
          <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-4">
            <ReceiptPreview r={r} branding={settings?.branding} />
          </div>
        </div>
      </div>

      <div className="mt-4 flex gap-2 border-t border-border pt-3">
        <Button className="h-11 flex-1 rounded-xl" onClick={save} disabled={saving} data-testid="receipt-save"><Save className="mr-1 h-4 w-4" />{saving ? 'Saving…' : 'Save receipt settings'}</Button>
        <Button variant="secondary" className="h-11 rounded-xl" onClick={reset} data-testid="receipt-reset"><RotateCcw className="mr-1 h-4 w-4" />Defaults</Button>
      </div>
    </div>
  );
}

const ToggleRow = ({ label, checked, onChange, testId }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-muted-foreground">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);
